import React, { useEffect, useState } from 'react'

import { useFreeSpinsBonusesStore } from '@stores'
import { BSModalButton } from '@components'
import { useTranslation } from '@amusnet/translations/react'
import { BonusSystemState, GameUIMessageType, BonusesPlayLaterMessage } from 'game-ui-types'
import { BonusState } from '@amusnet/bonuses-http-client'

import { isFlexiBundle, isSingleOptionFlexiBundle, showFlexiBundles } from '@amusnet/react-ui'
import { FreeSpinsBonus } from '@amusnet/mgs-ws-client'

interface FSMainButtonsProps {
    bonus?: FreeSpinsBonus
    disablePlayNowButton?: boolean
}

const FSMainButtons: React.FC<FSMainButtonsProps> = ({ bonus, disablePlayNowButton = false }: FSMainButtonsProps) => {
    const bonusSystemState = useFreeSpinsBonusesStore((state) => state.bonusSystemState)
    const setBonusSystemState = useFreeSpinsBonusesStore((state) => state.setBonusSystemState)
    const bonuses = useFreeSpinsBonusesStore((state) => state.bonuses)
    const selectFlexiBonusOption = useFreeSpinsBonusesStore((state) => state.selectFlexiBonusOption)
    const selectedFlexiBonusOption = useFreeSpinsBonusesStore((state) => state.selectedFlexiBonusOption)
    const setFlexiQueue = useFreeSpinsBonusesStore((state) => state.setFlexiQueue)
    const flexiQueue = useFreeSpinsBonusesStore((state) => state.flexiQueue)
    const setNonFlexiBonuses = useFreeSpinsBonusesStore((state) => state.setNonFlexiBonuses)
    const removeBonusFromFlexiQueue = useFreeSpinsBonusesStore((state) => state.removeBonusFromFlexiQueue)

    const { t } = useTranslation()

    const [primaryButtonText, setPrimaryButtonText] = useState('')
    const [secondaryButtonText, setSecondaryButtonText] = useState('')

    const isInSingleBonusState = bonusSystemState === BonusSystemState.SINGLE_BONUS_INITIAL
    const isInMultipleBonusesState = bonusSystemState === BonusSystemState.MULTIPLE_BONUSES_INITIAL
    const canPlayLater = isInSingleBonusState ? bonus?.playLater : bonuses.every((b) => b.playLater)

    useEffect(() => {
        switch (bonusSystemState) {
            case BonusSystemState.SINGLE_BONUS_INITIAL: {
                setPrimaryButtonText(t('gameui.playNow'))
                setSecondaryButtonText(t('gameui.playLater'))
                break
            }
            case BonusSystemState.MULTIPLE_BONUSES_INITIAL: {
                setPrimaryButtonText(t('gameui.playAllNow'))
                setSecondaryButtonText(t('gameui.playAllLater'))
                break
            }
        }
    }, [bonusSystemState])

    const playNowHandler = () => {
        if (isInMultipleBonusesState) {
            const multiOptionFlexiBonuses = bonuses.filter(
                (b) =>
                    isFlexiBundle(b) &&
                    b.flexiBonusOptions &&
                    b.flexiBonusOptions.length > 1 &&
                    b.state === BonusState.AWARDED
            )

            if (multiOptionFlexiBonuses.length > 0) {
                const normalBonuses = bonuses.filter(
                    (b) => (!isFlexiBundle(b) || isSingleOptionFlexiBundle(b)) && b.state === BonusState.AWARDED
                )

                setNonFlexiBonuses(normalBonuses)
                setFlexiQueue(multiOptionFlexiBonuses)

                return
            }
        }

        // Single bonus modal "Play Now" action OR multi-play with zero multi-option flexi bonuses
        const activeBonuses =
            isInSingleBonusState && bonus ? [bonus] : bonuses.filter((b) => b.state === BonusState.AWARDED)

        // 1. Send all normal non-flexi bonuses together in 1 batch message
        const normalBonusIds = activeBonuses.filter((b) => !isFlexiBundle(b)).map((b) => b.id)

        if (normalBonusIds.length > 0) {
            window.uiBus.postMessage({
                type: GameUIMessageType.BONUSES_PLAY_NOW,
                payload: { bonusIds: normalBonusIds },
            })
        }

        // 2. Send an individual message for EACH single-option flexi bonus
        const flexiBonuses = activeBonuses.filter((b) => isFlexiBundle(b) && b.state === BonusState.AWARDED)

        flexiBonuses.forEach((b) => {
            const flexiOption = isSingleOptionFlexiBundle(b) ? b.flexiBonusOptions?.[0] : selectedFlexiBonusOption

            window.uiBus.postMessage({
                type: GameUIMessageType.BONUSES_PLAY_NOW,
                payload: {
                    bonusIds: [b.id],
                    ...(showFlexiBundles && flexiOption != null
                        ? {
                              flexiBonusOption: flexiOption,
                          }
                        : {}),
                },
            })
        })

        setBonusSystemState(BonusSystemState.IN_PLAY)

        if (isInSingleBonusState && bonus) {
            removeBonusFromFlexiQueue(bonus.id)
        }

        if (showFlexiBundles) {
            selectFlexiBonusOption(null)
        }
    }

    const playLaterHandler = () => {
        if (isInSingleBonusState && !bonus) {
            return
        }

        if (showFlexiBundles && selectedFlexiBonusOption) {
            selectFlexiBonusOption(null)
        }

        if (isInSingleBonusState || isInMultipleBonusesState) {
            const isCurrentBonusFlexi = Boolean(
                isInSingleBonusState &&
                bonus &&
                isFlexiBundle(bonus) &&
                bonus.flexiBonusOptions &&
                bonus.flexiBonusOptions!.length > 0
            )

            const bonusIds = isCurrentBonusFlexi && bonus ? [bonus.id] : bonuses.map((b) => b.id)

            const message: BonusesPlayLaterMessage = {
                type: GameUIMessageType.BONUSES_PLAY_LATER,
                payload: { bonusIds },
            }

            window.uiBus.postMessage(message)

            if (isInSingleBonusState && bonus) {
                removeBonusFromFlexiQueue(bonus.id)
            }

            const hasRemainingFlexiBonuses = Boolean(flexiQueue && flexiQueue.length > 1)

            if (!hasRemainingFlexiBonuses) {
                setBonusSystemState(BonusSystemState.INACTIVE)
            }
        }
    }

    return (
        <>
            <div className="flex justify-center gap-[20px] w-full px-[77px] pt-[24px] pb-[24px]">
                {canPlayLater && (
                    <BSModalButton
                        id={secondaryButtonText.toLowerCase().replace(/\s+/g, '-')}
                        text={secondaryButtonText}
                        isPrimary={false}
                        onClick={playLaterHandler}
                    />
                )}
                <BSModalButton
                    id={primaryButtonText.toLowerCase().replace(/\s+/g, '-')}
                    text={primaryButtonText}
                    onClick={playNowHandler}
                    isDisabled={disablePlayNowButton}
                />
            </div>
        </>
    )
}

export default FSMainButtons
